/**
 * Характеристики при создании (SRD 5.2, с. 21).
 *
 * Три способа получить набор из шести чисел — стандартный набор,
 * броски, покупка очков — и прибавки предыстории поверх. Числа
 * способов приходят из записи правил (`content/srd/rules`), а не
 * лежат здесь: захардкоженный бюджет в 27 очков означал бы второе
 * место, где хранятся правила.
 *
 * Костей движок не бросает (D21.4). Броски приходят готовыми числами,
 * и проверяется только то, что такие числа этим броском получаются.
 */
export const ABILITY_NAMES = [
    'strength',
    'dexterity',
    'constitution',
    'intelligence',
    'wisdom',
    'charisma',
];
/** Короткий ключ характеристики в контексте расчёта: `str`, `dex`. */
export const ABILITY_KEYS = {
    strength: 'str',
    dexterity: 'dex',
    constitution: 'con',
    intelligence: 'int',
    wisdom: 'wis',
    charisma: 'cha',
};
/** «Fill in the ability modifiers as well» (с. 21). */
export function abilityModifier(score) {
    return Math.floor((score - 10) / 2);
}
/** Границы, которые даёт бросок: сумма оставленных костей. */
export function rollBounds(roll) {
    return { min: roll.keep, max: roll.keep * roll.die };
}
function scores(assignment) {
    return ABILITY_NAMES.map((name) => assignment[name]);
}
function sorted(values) {
    return [...values].sort((left, right) => left - right);
}
function sameMultiset(left, right) {
    if (left.length !== right.length)
        return false;
    const a = sorted(left);
    const b = sorted(right);
    return a.every((value, index) => value === b[index]);
}
function problem(code, message) {
    return { slot: 'abilities', code, message };
}
/** Стоимость счёта по таблице источника; `null` — счёт не покупается. */
export function pointCost(methods, score) {
    return methods.pointBuy.costs.find((row) => row.score === score)?.cost ?? null;
}
/**
 * Потрачено очков на набор — и сколько осталось от бюджета.
 *
 * Считается здесь, а не в интерфейсе: остаток бюджета — правило
 * источника (с. 21), и второе место, где он считается, однажды
 * разойдётся с проверкой (CLAUDE.md, п. 5). `spent` не определено
 * ровно тогда, когда хоть один счёт не покупается вовсе, — тогда
 * складывать нечего, и об этом скажет `validateAbilities`.
 */
export function pointBuySpending(methods, assignment) {
    let spent = 0;
    for (const name of ABILITY_NAMES) {
        const cost = pointCost(methods, assignment[name]);
        if (cost === null)
            return { spent: null, remaining: null };
        spent += cost;
    }
    return { spent, remaining: methods.pointBuy.budget - spent };
}
export function validateAbilities(choice, methods) {
    const problems = [];
    const chosen = scores(choice.assignment);
    if (choice.method === 'standard_array') {
        if (!sameMultiset(chosen, methods.standardArray)) {
            problems.push(problem('not-standard-array', `стандартный набор — ${methods.standardArray.join(', ')}; ` +
                `назначено ${[...sorted(chosen)].reverse().join(', ')}`));
        }
        return problems;
    }
    if (choice.method === 'point_buy') {
        for (const name of ABILITY_NAMES) {
            const score = choice.assignment[name];
            if (pointCost(methods, score) === null) {
                problems.push(problem('score-not-purchasable', `значение ${score} (${name}) нельзя купить: в таблице стоимости ` +
                    `есть только ${methods.pointBuy.costs.map((row) => row.score).join(', ')}`));
            }
        }
        const { spent } = pointBuySpending(methods, choice.assignment);
        if (problems.length === 0 && spent !== null && spent > methods.pointBuy.budget) {
            problems.push(problem('point-buy-over-budget', `на покупку потрачено ${spent} очков при бюджете ${methods.pointBuy.budget}`));
        }
        return problems;
    }
    const { min, max } = rollBounds(methods.roll);
    if (choice.rolls.length !== methods.roll.count) {
        problems.push(problem('wrong-roll-count', `бросков должно быть ${methods.roll.count}, получено ${choice.rolls.length}`));
    }
    for (const value of choice.rolls) {
        if (value < min || value > max) {
            problems.push(problem('roll-out-of-range', `бросок ${value} невозможен: ${methods.roll.dice}d${methods.roll.die} ` +
                `с оставленными ${methods.roll.keep} даёт от ${min} до ${max}`));
        }
    }
    if (problems.length === 0 && !sameMultiset(chosen, choice.rolls)) {
        problems.push(problem('assignment-mismatch', `назначены не те числа, что выпали: выпало ${sorted(choice.rolls).join(', ')}, ` +
            `назначено ${sorted(chosen).join(', ')}`));
    }
    return problems;
}
/**
 * Какие прибавки ещё можно раздать, если часть уже роздана.
 *
 * Раскладки — альтернативы: «+2 и +1» **либо** «+1 всем трём»
 * (SRD 5.2, с. 21). Значит, выбранная двойка отсекает вторую
 * раскладку целиком, а не просто вычитается из списка. Отсюда правило:
 * раскладка годится, если из неё вычитается всё уже розданное; остаток
 * годных раскладок и есть то, что можно взять дальше.
 *
 * Живёт здесь, а не в поле формы, по той же причине, что и всё
 * остальное правило: список доступного и проверка присланного обязаны
 * быть согласованы, а согласованы они бывают только когда это один код
 * (CLAUDE.md, п. 5). Иначе форма предложит вариант, который сервер
 * не примет.
 */
export function increaseRemainder(patterns, taken) {
    const left = new Set();
    for (const pattern of patterns) {
        const rest = [...pattern];
        let fits = true;
        for (const amount of taken) {
            const at = rest.indexOf(amount);
            if (at === -1) {
                fits = false;
                break;
            }
            rest.splice(at, 1);
        }
        if (fits)
            for (const amount of rest)
                left.add(amount);
    }
    return [...left].sort((a, b) => a - b);
}
export function validateIncrease(increase, assignment, plan) {
    const problems = [];
    const slot = 'increase';
    const amounts = increase.map((item) => item.amount).sort((a, b) => b - a);
    const matches = plan.patterns.some((pattern) => {
        const wanted = [...pattern].sort((a, b) => b - a);
        return sameMultiset(amounts, wanted);
    });
    if (!matches) {
        problems.push({
            slot,
            code: 'increase-pattern',
            message: `прибавки предыстории задаются как ${plan.patterns
                .map((pattern) => pattern.join('+'))
                .join(' либо ')}; получено ${amounts.join('+') || '(пусто)'}`,
        });
    }
    const seen = new Set();
    for (const item of increase) {
        if (seen.has(item.ability)) {
            problems.push({
                slot,
                code: 'increase-pattern',
                message: `характеристика ${item.ability} увеличена дважды`,
            });
        }
        seen.add(item.ability);
        if (!plan.abilities.includes(item.ability)) {
            problems.push({
                slot,
                code: 'increase-not-offered',
                message: `предыстория даёт прибавку к ${plan.abilities.join(', ')}, ` +
                    `а не к ${item.ability}`,
            });
            continue;
        }
        const result = assignment[item.ability] + item.amount;
        if (result > plan.maxScore) {
            problems.push({
                slot,
                code: 'increase-over-max',
                message: `${item.ability} стала бы ${result} при пределе ${plan.maxScore} ` +
                    '(«None of these increases can raise a score above 20»)',
            });
        }
    }
    return problems;
}
/** Значения после прибавок предыстории — то, что попадает в контекст. */
export function finalScores(assignment, increase) {
    const result = { ...assignment };
    for (const item of increase) {
        result[item.ability] = result[item.ability] + item.amount;
    }
    return result;
}
//# sourceMappingURL=abilities.js.map